import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { Users, UserCheck, CreditCard, Activity, Euro, TrendingUp, AlertTriangle, Send, Tag, BarChart3, LayoutDashboard, Megaphone, Store, UserPlus } from 'lucide-react';
import { motion } from 'framer-motion';
import Analytics from './Analytics';
import ContentChangeRequests from '../../components/Admin/ContentChangeRequests';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalClients: 0,
    totalVendors: 0,
    totalSubscriptions: 0,
    activeSubscriptions: 0,
    expiringSoon: 0,
    totalRevenue: 0,
    monthlyRevenue: 0,
    pendingVendors: 0,
    pendingPayouts: 0,
    totalCredentials: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, purchasesRes, influencersRes] = await Promise.all([
        api.get('/admin/dashboard-stats'),
        api.get('/admin/recent-purchases?limit=5').catch(() => ({ data: { purchases: [] } })),
        api.get('/admin/influencer-stats').catch(() => ({ data: { overview: {} } }))
      ]);

      const s = statsRes.data || {};
      const totalRevenue = (s.totalSubscriptions || 0) * 59;
      const infOverview = influencersRes.data.overview || {};

      setStats({
        totalUsers: s.totalUsers || 0,
        totalClients: s.totalClients || 0,
        totalVendors: s.totalVendors || 0,
        totalSubscriptions: s.totalSubscriptions || 0,
        activeSubscriptions: s.activeSubscriptions || 0,
        expiringSoon: 0,
        totalRevenue: totalRevenue,
        monthlyRevenue: (totalRevenue / 12).toFixed(0),
        pendingVendors: s.pendingVendors || 0,
        pendingPayouts: 0,
        totalCredentials: 0,
        totalInfluencers: infOverview.total_influencers || 0,
        activeInfluencers: infOverview.active_influencers || 0,
        influencerSales: infOverview.total_sales || 0
      });
      
      setRecentActivity(purchasesRes.data.purchases || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[#FF6B35] text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8" data-testid="admin-dashboard">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">Tableau de Bord</h1>
        <p className="text-sm text-[#B8B8BA]">Vue d'ensemble IPTV4KFR</p>
      </motion.div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-[#121214] p-1 rounded-lg border border-[#3A3A3D]/40 w-fit" data-testid="dashboard-tabs">
        <button onClick={() => setActiveTab('overview')}
          className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-medium transition-all ${activeTab === 'overview' ? 'bg-[#FF6B35] text-black' : 'text-[#B8B8BA] hover:text-white hover:bg-[#1A1A1D]'}`} data-testid="tab-overview">
          <LayoutDashboard className="h-4 w-4" />Vue d'ensemble
        </button>
        <button onClick={() => setActiveTab('analytics')}
          className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-medium transition-all ${activeTab === 'analytics' ? 'bg-[#FF6B35] text-black' : 'text-[#B8B8BA] hover:text-white hover:bg-[#1A1A1D]'}`} data-testid="tab-analytics">
          <BarChart3 className="h-4 w-4" />Analytics
        </button>
      </div>

      {activeTab === 'analytics' ? (
        <Analytics />
      ) : (
      <>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-6 mb-6 sm:mb-8">
        <Link to="/admin/clients" className="bg-[#1A1A1D] p-4 sm:p-6 rounded-lg metallic-border hover:border-[#FF6B35] transition-all" data-testid="stat-clients">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[#B8B8BA] text-xs sm:text-sm">Clients</span>
            <Users className="h-4 w-4 sm:h-5 sm:w-5 text-[#FF6B35]" />
          </div>
          <div className="text-2xl sm:text-4xl font-bold text-white mb-1">{stats.totalClients}</div>
          <div className="text-[#00FF88] text-xs sm:text-sm">Actifs</div>
        </Link>

        <Link to="/admin/vendors" className="bg-[#1A1A1D] p-4 sm:p-6 rounded-lg metallic-border hover:border-[#FF6B35] transition-all" data-testid="stat-vendors">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[#B8B8BA] text-xs sm:text-sm">Vendeurs</span>
            <UserCheck className="h-4 w-4 sm:h-5 sm:w-5 text-[#FFB800]" />
          </div>
          <div className="text-2xl sm:text-4xl font-bold text-white mb-1">{stats.totalVendors}</div>
          {stats.pendingVendors > 0 && (
            <div className="text-[#FFB800] text-xs sm:text-sm flex items-center">
              <AlertTriangle className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
              {stats.pendingVendors} en attente
            </div>
          )}
        </Link>

        <Link to="/admin/influencers" className="bg-[#1A1A1D] p-4 sm:p-6 rounded-lg metallic-border hover:border-[#FF6B35] transition-all" data-testid="stat-influencers">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[#B8B8BA] text-xs sm:text-sm">Influenceurs</span>
            <Megaphone className="h-4 w-4 sm:h-5 sm:w-5 text-[#00FFFF]" />
          </div>
          <div className="text-2xl sm:text-4xl font-bold text-white mb-1">{stats.totalInfluencers}</div>
          <div className="text-[#00FFFF] text-xs sm:text-sm">{stats.activeInfluencers} actif{stats.activeInfluencers > 1 ? 's' : ''} · {stats.influencerSales} vente{stats.influencerSales > 1 ? 's' : ''}</div>
        </Link>

        <div className="bg-[#1A1A1D] p-4 sm:p-6 rounded-lg metallic-border" data-testid="stat-subscriptions">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[#B8B8BA] text-xs sm:text-sm">Abos</span>
            <CreditCard className="h-4 w-4 sm:h-5 sm:w-5 text-[#FF6B35]" />
          </div>
          <div className="text-2xl sm:text-4xl font-bold text-white mb-1">{stats.activeSubscriptions}</div>
          <div className="text-[#B8B8BA] text-xs sm:text-sm">/{stats.totalSubscriptions} total</div>
        </div>

        <div className="bg-[#1A1A1D] p-4 sm:p-6 rounded-lg metallic-border" data-testid="stat-revenue">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[#B8B8BA] text-xs sm:text-sm">CA Total</span>
            <Euro className="h-4 w-4 sm:h-5 sm:w-5 text-[#00FF88]" />
          </div>
          <div className="text-2xl sm:text-4xl font-bold text-[#00FF88] mb-1">{stats.totalRevenue}€</div>
          <div className="text-[#B8B8BA] text-xs sm:text-sm">{stats.monthlyRevenue}€/mois</div>
        </div>
      </div>

      {/* Demandes de modification de contenu (clients) */}
      <ContentChangeRequests />

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8 mb-6 sm:mb-8">
        {/* Recent Activity */}
        <div className="lg:col-span-2 bg-[#1A1A1D] p-4 sm:p-8 rounded-lg metallic-border">
          <h2 className="text-lg sm:text-2xl font-bold text-white mb-4 sm:mb-6">Activité récente</h2>
          <div className="space-y-2 sm:space-y-3">
            {recentActivity.length === 0 ? (
              <p className="text-[#B8B8BA] text-center py-6 sm:py-8 text-sm">Aucune activité récente</p>
            ) : (
              recentActivity.map((activity, index) => (
                <div key={index} className="bg-[#222226] p-3 sm:p-4 rounded-lg flex items-center justify-between">
                  <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                    <div className="h-8 w-8 sm:h-10 sm:w-10 bg-[#FF6B35]/10 rounded-full flex items-center justify-center shrink-0">
                      <Euro className="h-4 w-4 sm:h-5 sm:w-5 text-[#FF6B35]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-white font-semibold text-sm sm:text-base truncate">{activity.user_name}</div>
                      <div className="text-[#6B6B6E] text-xs truncate">{activity.user_email}</div>
                      {activity.promo_code && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <Tag className="h-3 w-3 text-[#FF6B35]" />
                          <span className="text-[#FF6B35] text-xs font-mono">{activity.promo_code}</span>
                          <span className="text-[#00FF88] text-xs">-{activity.promo_discount}€</span>
                        </div>
                      )}
                      {(activity.vendor_name || activity.referrer_name) && (
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          {activity.vendor_name && (
                            <span className="flex items-center gap-1 text-xs text-[#FFB800]">
                              <Store className="h-3 w-3" />{activity.vendor_name}
                            </span>
                          )}
                          {activity.referrer_name && (
                            <span className="flex items-center gap-1 text-xs text-[#00FFFF]">
                              <UserPlus className="h-3 w-3" />{activity.referrer_name}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-right ml-2">
                    <div className="text-[#00FF88] font-bold text-sm sm:text-lg">{activity.amount_paid}€</div>
                    {activity.promo_discount > 0 && (
                      <div className="text-[#6B6B6E] text-xs line-through">{activity.original_amount}€</div>
                    )}
                    <div className="text-[#6B6B6E] text-xs">{formatDate(activity.date)}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-gradient-to-br from-[#1A1A1D] to-[#121214] p-4 sm:p-8 rounded-lg metallic-border cyber-glow">
          <h2 className="text-lg sm:text-2xl font-bold text-white mb-4 sm:mb-6">Actions Rapides</h2>
          <div className="space-y-2 sm:space-y-3">
            <Link to="/admin/clients" className="block w-full btn-primary text-center text-sm py-2.5" data-testid="goto-clients">
              <Users className="inline-block mr-2 h-4 w-4" />
              Clients
            </Link>
            <Link to="/admin/vendors" className="block w-full btn-secondary text-center text-sm py-2.5" data-testid="goto-vendors">
              <UserCheck className="inline-block mr-2 h-4 w-4" />
              Vendeurs
            </Link>
            <Link to="/admin/influencers" className="block w-full btn-secondary text-center text-sm py-2.5" data-testid="goto-influencers">
              <TrendingUp className="inline-block mr-2 h-4 w-4" />
              Influenceurs
            </Link>
            <Link to="/admin/settings" className="block w-full btn-secondary text-center text-sm py-2.5" data-testid="goto-settings">
              <Send className="inline-block mr-2 h-4 w-4" />
              Paramètres
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
        {/* Key Indicators */}
        <div className="bg-[#1A1A1D] p-4 sm:p-8 rounded-lg metallic-border">
          <h3 className="text-base sm:text-xl font-bold text-white mb-3 sm:mb-4">Indicateurs Clés</h3>
          <div className="space-y-2 sm:space-y-4">
            <div className="flex items-center justify-between p-3 sm:p-4 bg-[#222226] rounded-lg">
              <span className="text-[#B8B8BA] text-sm">Taux de conversion</span>
              <span className="text-[#00FF88] font-bold text-sm sm:text-lg">78%</span>
            </div>
            <div className="flex items-center justify-between p-3 sm:p-4 bg-[#222226] rounded-lg">
              <span className="text-[#B8B8BA] text-sm">Clients avec parrainage</span>
              <span className="text-[#FF6B35] font-bold text-sm sm:text-lg">40%</span>
            </div>
            <div className="flex items-center justify-between p-3 sm:p-4 bg-[#222226] rounded-lg">
              <span className="text-[#B8B8BA] text-sm">Renouvellements auto</span>
              <span className="text-[#FFB800] font-bold text-sm sm:text-lg">65%</span>
            </div>
          </div>
        </div>

        {/* Alerts */}
        <div className="bg-[#1A1A1D] p-4 sm:p-8 rounded-lg metallic-border">
          <h3 className="text-base sm:text-xl font-bold text-white mb-3 sm:mb-4 flex items-center space-x-2">
            <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-[#FFB800]" />
            <span>Alertes</span>
          </h3>
          <div className="space-y-2 sm:space-y-3">
            {stats.pendingVendors > 0 && (
              <Link to="/admin/vendors" className="block p-3 sm:p-4 bg-[#FFB800]/10 border border-[#FFB800] rounded-lg">
                <div className="text-[#FFB800] font-semibold text-sm">{stats.pendingVendors} demande(s) vendeur</div>
                <div className="text-[#B8B8BA] text-xs mt-1">Cliquez pour approuver</div>
              </Link>
            )}
            {stats.pendingPayouts > 0 && (
              <Link to="/admin/vendors" className="block p-3 sm:p-4 bg-[#FF3B5C]/10 border border-[#FF3B5C] rounded-lg">
                <div className="text-[#FF3B5C] font-semibold text-sm">{stats.pendingPayouts} paiement(s)</div>
                <div className="text-[#B8B8BA] text-xs mt-1">Cliquez pour traiter</div>
              </Link>
            )}
            {stats.expiringSoon > 0 && (
              <div className="p-3 sm:p-4 bg-[#FF6B35]/10 border border-[#FF6B35] rounded-lg">
                <div className="text-[#FF6B35] font-semibold text-sm">{stats.expiringSoon} abo(s) expire(nt)</div>
                <div className="text-[#B8B8BA] text-xs mt-1">Dans les 7 prochains jours</div>
              </div>
            )}
            {stats.pendingVendors === 0 && stats.pendingPayouts === 0 && (
              <div className="p-6 sm:p-8 text-center">
                <Activity className="h-10 w-10 sm:h-12 sm:w-12 text-[#00FF88] mx-auto mb-3" />
                <p className="text-[#00FF88] font-semibold text-sm">Tout est à jour !</p>
                <p className="text-[#B8B8BA] text-xs mt-1">Aucune action requise</p>
              </div>
            )}
          </div>
        </div>
      </div>
      </>
      )}
    </div>
  );
};

export default AdminDashboard;
